import {
  Box,
  Flex,
  NumberFormatter,
  SimpleGrid,
  Text,
  Title,
} from "@mantine/core";
import classes from "./index.module.css";
import { TCalulcationResponse } from "../../hooks/api/useGetCalculationById";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

const PensionOverviewStack = ({ data }: { data: TCalulcationResponse }) => {
  const retirementAge = data.parameters.statePensionAge;
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" verticalSpacing="lg">
      <Box>
        {data.paymentDistribution.P ? (
          <>
            <Text c="dimmed" size="xs">
              Privatpension
            </Text>

            <Flex
              mih={"25px"}
              h={`${data.paymentDistribution.P_Percentage * 5}px`}
              bg={
                "repeating-linear-gradient(45deg,rgb(255, 228, 188),rgb(255, 228, 188) 2px,rgba(255, 228, 188, 0.5) 2px,rgba(255, 228, 188, 0.5) 4px"
              }
              justify={"center"}
              align={"center"}
              className={classes.radiusTop}
              p={"sm"}
              fz={"14px"}
            >
              <NumberFormatter
                value={data.paymentDistribution.P_Percentage}
                suffix={" %"}
                decimalScale={0}
              />
            </Flex>
          </>
        ) : null}

        <>
          {data.paymentDistribution.T_DC_Percentage ||
          data.paymentDistribution.T_NonDC_Percentage ? (
            <Text c="dimmed" size="xs" mt={"md"}>
              Tjänstepension
            </Text>
          ) : null}

          {data.paymentDistribution.T_DC_Percentage ? (
            <Flex
              h={`${data.paymentDistribution.T_DC_Percentage * 5}px`}
              bg={
                "repeating-linear-gradient(45deg,rgb(255, 223, 225),rgb(255, 223, 225) 2px,rgba(255, 223, 225, 0.5) 2px,rgba(255, 223, 225, 0.5) 4px)"
              }
              justify={"center"}
              align={"center"}
              p={"sm"}
              fz={"14px"}
              className={
                !data.paymentDistribution.P_Percentage ? classes.radiusTop : ""
              }
            >
              Premiebestämd &nbsp;
              <NumberFormatter
                value={data.paymentDistribution.T_DC_Percentage}
                suffix={" %"}
                decimalScale={0}
              />
            </Flex>
          ) : null}
          {data.paymentDistribution.T_NonDC_Percentage ? (
            <Flex
              h={`${data.paymentDistribution.T_NonDC_Percentage * 5}px`}
              bg={"var(--wallet-color-sambla-red-shade-400)"}
              justify={"center"}
              align={"center"}
              p={"sm"}
              fz={"14px"}
            >
              Förmånsbästämd &nbsp;
              <NumberFormatter
                value={data.paymentDistribution.T_NonDC_Percentage}
                suffix={" %"}
                decimalScale={0}
              />
            </Flex>
          ) : null}
        </>

        <>
          {data.paymentDistribution.PPM_Percentage ||
          data.paymentDistribution.I_Percentage ||
          data.paymentDistribution.G_Percentage ? (
            <Text c="dimmed" size="xs" mt="sm">
              Allmän Pension
            </Text>
          ) : null}
          {data.paymentDistribution.PPM_Percentage ? (
            <Flex
              h={`${data.paymentDistribution.PPM_Percentage * 5}px`}
              bg={
                "repeating-linear-gradient(45deg,rgb(196, 236, 213),rgb(196, 236, 213) 2px,rgba(196, 236, 213, 0.5) 2px,rgba(196, 236, 213, 0.5) 4px)"
              }
              justify={"center"}
              align={"center"}
              p={"sm"}
              fz={"14px"}
            >
              Premiepension &nbsp;
              <NumberFormatter
                value={data.paymentDistribution.PPM_Percentage}
                suffix={" %"}
                decimalScale={0}
              />
            </Flex>
          ) : null}
          {data.paymentDistribution.I_Percentage ? (
            <Flex
              h={`${data.paymentDistribution.I_Percentage * 5}px`}
              bg={"var(--wallet-color-sambla-green-shade-300)"}
              justify={"center"}
              align={"center"}
              p={"sm"}
              fz={"14px"}
            >
              Inkomstpension &nbsp;
              <NumberFormatter
                value={data.paymentDistribution.I_Percentage}
                suffix={" %"}
                decimalScale={0}
              />
            </Flex>
          ) : null}
          {data.paymentDistribution.G_Percentage ? (
            <Flex
              h={`${data.paymentDistribution.G_Percentage * 5}px`}
              bg={"var(--wallet-color-sambla-green-shade-500)"}
              justify={"center"}
              align={"center"}
              className={classes.radiusBottom}
              p={"sm"}
              fz={"14px"}
            >
              Garantipension &nbsp;
              <NumberFormatter
                value={data.paymentDistribution.G_Percentage}
                suffix={" %"}
                decimalScale={0}
              />
            </Flex>
          ) : null}
        </>
      </Box>
      <Flex direction={"column"}>
        <Box mb={"md"}>
          <Text c="dimmed">Totalt pensionskapital</Text>
          <Title order={2} size="h4">
            <NumberFormatter
              className={REPLAY_BLOCK_CLASS}
              value={data.totalAllocation.sumOfAssets}
              decimalScale={0}
              thousandSeparator={" "}
              suffix=" SEK"
            />
          </Title>
        </Box>
        {data?.managedAllocation && (
          <Box mb={"md"}>
            <Text c="dimmed">Varav under rådgiven förvaltning av Sambla</Text>
            <Title order={2} size="h4">
              <NumberFormatter
                className={REPLAY_BLOCK_CLASS}
                value={data?.managedAllocation?.totalAssetValue ?? 0}
                decimalScale={0}
                thousandSeparator={" "}
                suffix=" SEK"
                prefix={"ca "}
              />
            </Title>
          </Box>
        )}
        <Box mb={"md"}>
          <Text c="dimmed">Förväntad pension vid {retirementAge} år</Text>
          <Title order={2} size="h4">
            <NumberFormatter
              className={REPLAY_BLOCK_CLASS}
              value={data?.paymentDistribution?.firstMonthPension ?? 0}
              decimalScale={0}
              thousandSeparator={" "}
              suffix=" SEK"
              prefix={"ca "}
            />
          </Title>
        </Box>

        <Box mb={"md"}>
          <Text c="dimmed">Varav garanterad pension</Text>
          <Title order={2} size="h4">
            <NumberFormatter
              className={REPLAY_BLOCK_CLASS}
              value={data.paymentDistribution.guaranteePension || 0}
              decimalScale={0}
              thousandSeparator={" "}
              suffix=" SEK"
              prefix={"ca "}
            />
          </Title>
        </Box>
        <Box mb={"md"}>
          <Text c="dimmed">Andel som påverkas av börsen</Text>
          <Flex>
            <Title order={2} size="h4" mr="8px">
              <NumberFormatter
                value={data.paymentDistribution.affectedByRisk || 0}
                decimalScale={2}
                suffix=" %"
              />
            </Title>
            {data.paymentDistribution.PPM_Percentage ||
            data.paymentDistribution.I_Percentage ||
            data.paymentDistribution.G_Percentage ? (
              <Box
                h="24px"
                w="24px"
                bg={
                  "repeating-linear-gradient(45deg,rgb(196, 236, 213),rgb(196, 236, 213) 2px,rgba(196, 236, 213, 0.5) 2px,rgba(196, 236, 213, 0.5) 4px)"
                }
                mr={"5px"}
                style={{ borderRadius: "25px" }}
              />
            ) : null}
            {data.paymentDistribution.T_DC_Percentage ||
            data.paymentDistribution.T_NonDC_Percentage ? (
              <Box
                h="24px"
                w="24px"
                mr={"5px"}
                bg={
                  "repeating-linear-gradient(45deg,rgb(255, 223, 225),rgb(255, 223, 225) 2px,rgba(255, 223, 225, 0.5) 2px,rgba(255, 223, 225, 0.5) 4px)"
                }
                style={{ borderRadius: "25px" }}
              />
            ) : null}
            {data.paymentDistribution.P ? (
              <Box
                h="24px"
                w="24px"
                mr={"3px"}
                bg={
                  "repeating-linear-gradient(45deg,rgb(255, 228, 188),rgb(255, 228, 188) 2px,rgba(255, 228, 188, 0.5) 2px,rgba(255, 228, 188, 0.5) 4px"
                }
                style={{ borderRadius: "25px" }}
              />
            ) : null}
          </Flex>
        </Box>
      </Flex>
    </SimpleGrid>
  );
};

export default PensionOverviewStack;
